import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import GpoFilter, { matchesGpo, FilterCount } from './GpoFilter';
import { printDocument, printBtnStyle } from '../utils/print';
import RiskAlertBanner from './RiskAlertBanner';
import { ListFooter } from '../ui';
import { reportError } from '../utils/reportError';

// PPM + Production File Checklist (Order Execution Phase 3). One screen, two tabs,
// one combined print pack. PPM creation is gated behind Full Kitting (Phase 2).

const NAVY = '#2D2C72', INK = '#3B3A8F', RED = '#E22D2D', WARM = '#F7F6F3', VIOLET = '#EEEDF8';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// PPM sections A–F (from the PPM format). Each item: particular label + a
// parameter value + comments. B & E also carry a "machine servicing required" flag.
const SECTIONS = [
  { key: 'A', title: 'A · Raw Material', items: [
    ['fabric_inspection', 'Fabric inspection (4-Point)'], ['fpt', 'Fabric Physical Test (FPT)'],
    ['shrinkage', 'Shrinkage / spirality'], ['size_set_pattern', 'Size-set pattern check'], ['trims_approval', 'Trims approval'],
  ] },
  { key: 'B', title: 'B · Cutting Parameters', machine: true, items: [
    ['inhouse_date', 'Fabric in-house date'], ['cut_date', 'Cutting date'], ['pilot_run', 'Pilot run'],
    ['bundle_size', 'Bundle size'], ['nap_direction', 'Nap direction (NOW / NUD)'], ['fabric_face', 'Fabric face (FOW / F2F)'],
    ['layer_height', 'Layer height'], ['bandknife_parts', 'Band-knife parts'], ['ticketing', 'Ticketing'],
    ['panel_check', 'Panel check'], ['fusing', 'Fusing'], ['moon_bone', 'Moon / bone'],
  ] },
  { key: 'C', title: 'C · Embroidery', items: [
    ['strike_off', 'Strike-off'], ['stitches', 'No. of stitches'], ['placement', 'Placement'],
    ['backing', 'Backing'], ['thread', 'Thread'], ['capacity', 'Capacity / day'],
  ] },
  { key: 'D', title: 'D · Printing', items: [
    ['description', 'Print description'], ['strike_off', 'Strike-off'], ['coats', 'No. of coats'], ['curing', 'Curing'],
    ['placement', 'Placement'], ['colours', 'No. of colours'], ['capacity', 'Capacity / day'],
  ] },
  { key: 'E', title: 'E · Stitching', machine: true, items: [
    ['needle', 'Needle'], ['spi', 'SPI'], ['thread_ply', 'Thread ply'], ['buttonhole', 'Buttonhole'], ['buttons', 'Buttons'],
    ['label_main', 'Main label'], ['label_size', 'Size label'], ['label_care', 'Care label'], ['zipper', 'Zipper'],
    ['placket', 'Placket'], ['collar', 'Collar'], ['pocket', 'Pocket'], ['drawcord', 'Drawcord'], ['special_folders', 'Special folders / attachments'],
  ] },
  { key: 'F', title: 'F · Quality & Finishing', items: [
    ['inline_check', 'Inline check'], ['final_check', 'Final check'], ['washing', 'Washing'], ['finishing', 'Finishing'],
    ['pressing', 'Pressing'], ['folding', 'Folding'], ['polypack', 'Polypack'], ['aql', 'AQL'], ['carton_packing', 'Carton packing'],
  ] },
];
const STAGES = [
  { n: 1, label: 'Stage 1 · Merchandising Team' }, { n: 2, label: 'Stage 2 · After PPM' },
  { n: 3, label: 'Stage 3 · Size Set Making' }, { n: 4, label: 'Stage 4 · Post-Shipment' },
];
const SIGNATURES = [
  ['style_merchant', 'Style Merchant'], ['cutting_incharge', 'Cutting Incharge'], ['qa_manager', 'QA Manager'],
  ['merchandising_manager', 'Merchandising Manager'], ['stitching_floor_incharge', 'Stitching Floor Incharge'], ['factory_manager', 'Factory Manager'],
];
const RESP_LABEL = { M: 'Merchant', Q: 'Quality', IE: 'IE', C: 'Cutting', S: 'Stores' };

const inputStyle = { padding: '6px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', width: '100%' };
const cardStyle = { background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: 18, marginBottom: 16 };
const titleStyle = { fontSize: 13, fontWeight: 700, color: INK, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px', paddingBottom: 6, borderBottom: `2px solid ${VIOLET}` };

export default function PPMScreen({ user, gpos: propGpos }) {
  const [gpos, setGpos] = useState(propGpos || []);
  const [list, setList] = useState([]);
  const [gpoQ, setGpoQ] = useState('');   // GET /ppm is UNCAPPED — client-side is safe
  const [open, setOpen] = useState(null);     // { ppm, header, items, gate, signals }
  const [pick, setPick] = useState('');
  const [tab, setTab] = useState('ppm');
  const [saving, setSaving] = useState(false);
  // editable working copies
  const [header, setHeader] = useState({});
  const [sections, setSections] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [sigs, setSigs] = useState({});

  const fetchList = useCallback(() => { api.get('/ppm').then((r) => setList(r.data.data || [])).catch(() => {}); }, []);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => {
    if (propGpos && propGpos.length) { setGpos(propGpos); return; }
    api.get('/oe/gpos').then((r) => setGpos(r.data.data || [])).catch(() => {});
  }, [propGpos]);

  const hydrate = (d) => {
    setOpen(d);
    setHeader(d.header || {});
    setSections((d.ppm && d.ppm.sections && Object.keys(d.ppm.sections).length) ? d.ppm.sections : {});
    setAttendance((d.ppm && Array.isArray(d.ppm.attendance)) ? d.ppm.attendance : []);
    setSigs((d.ppm && d.ppm.checklist_signatures) || {});
    setTab('ppm');
  };

  const openFor = async (gpoNo) => {
    setSaving(true);
    try {
      const r = await api.get('/ppm/by-gpo/' + gpoNo);
      const d = r.data.data;
      if (d.ppm) { hydrate(d); return; }
      if (!d.gate || !d.gate.allowed) { alert('🔒 PPM is blocked.\n\n' + ((d.gate && d.gate.reason) || 'Full Kitting must be complete first.')); return; }
      const c = await api.post('/ppm', { gpo_no: gpoNo });
      hydrate({ ...c.data.data, gate: d.gate, signals: d.signals });
      fetchList();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/ppm/' + open.ppm.id, { header, sections, attendance, checklist_signatures: sigs });
      setOpen((o) => ({ ...o, ppm: r.data.data }));
      alert('PPM saved.');
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const toggleItem = async (it, checked) => {
    try {
      const r = await api.post(`/ppm/items/${it.id}/check`, { checked });
      setOpen((o) => ({ ...o, items: o.items.map((x) => x.id === it.id ? r.data.data : x) }));
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };
  const setItemComment = (it, comments) => setOpen((o) => ({ ...o, items: o.items.map((x) => x.id === it.id ? { ...x, comments } : x) }));
  const saveItemComment = async (it) => { try { await api.post(`/ppm/items/${it.id}/check`, { comments: it.comments || '' }); } catch (e) { reportError('save this comment')(e); } };

  // section helpers
  const sec = (key) => sections[key] || {};
  const setSecVal = (key, type, ik, val) => setSections((s) => ({ ...s, [key]: { ...(s[key] || {}), [type]: { ...((s[key] || {})[type] || {}), [ik]: val } } }));
  const setSecField = (key, field, val) => setSections((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: val } }));

  const refreshAuto = async () => {
    try { const r = await api.post(`/ppm/${open.ppm.id}/refresh-auto`, {}); setOpen((o) => ({ ...o, items: r.data.data.items, signals: r.data.data.signals })); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const printPack = () => {
    const p = open.ppm, h = header || {};
    const secSections = SECTIONS.map((S) => ({
      title: `PPM ${S.title}${S.machine ? `  [Machine servicing: ${(sec(S.key).machine_servicing) || 'NA'}]` : ''}`,
      fields: [
        ...S.items.map(([ik, label]) => [label, [((sec(S.key).params || {})[ik] || '—'), ((sec(S.key).comments || {})[ik] ? '— ' + (sec(S.key).comments || {})[ik] : '')].filter(Boolean).join(' ')]),
        ['Signature', sec(S.key).signature || ''],
      ],
    }));
    const checklistSections = STAGES.map((st) => {
      const rows = open.items.filter((i) => i.stage === st.n);
      return rows.length ? { title: st.label, table: {
        columns: ['Item', 'Resp', 'Done', 'Auto', 'Comments'],
        rows: rows.map((i) => [i.item_name, i.responsibility || '', (i.checked || i.auto_checked) ? 'Yes' : 'No', i.auto_checked ? 'auto' : '', i.comments || '']),
      } } : null;
    }).filter(Boolean);
    printDocument({
      docTitle: 'Order Execution — PPM & Production File Checklist',
      subtitle: `${p.ppm_number || ''} · ${h.style_no || ''} · ${h.buyer || ''}`,
      meta: [['GPO', h.gpo_no || '—'], ['Style', h.style_no || '—'], ['Total Qty', h.total_qty || '—'], ['Status', (p.status || 'draft').toUpperCase()]],
      sections: [
        { title: 'PPM — Order Header', fields: [
          ['Buyer', h.buyer], ['Brand', h.brand], ['Style No', h.style_no], ['Style Ref', h.style_ref],
          ['Item', h.item_desc], ['Total Qty', h.total_qty], ['Delivery', fmtDate(h.delivery_date)],
        ] },
        (h.po_breakdown && h.po_breakdown.length) ? { title: 'PO Breakdown', table: {
          columns: ['PO', 'Buyer PO No', 'Qty', 'Combo', 'Delivery'],
          rows: h.po_breakdown.map((po) => [po.label || '', po.buyer_po_no || '', po.qty || '', po.combo || '', fmtDate(po.delivery_date)]),
        } } : null,
        (h.fabrics && h.fabrics.length) ? { title: 'Fabric Details', table: {
          columns: ['Name', 'Blend', 'GSM', 'Width', 'Finish', 'Wash', 'Target CPP'],
          rows: h.fabrics.map((f) => [f.name || '', f.blend || '', f.gsm || '', f.width || '', f.finish || '', f.wash || '', f.target_cpp || '']),
        } } : null,
        ...secSections,
        (attendance.length) ? { title: 'PPM Attendance', table: { columns: ['Name', 'Designation', 'Department'], rows: attendance.map((a) => [a.name || '', a.designation || '', a.department || '']) } } : null,
        { title: '— PRODUCTION FILE CHECKLIST —', fields: [['', '']] },
        ...checklistSections,
        { title: 'File Checklist Signatures', fields: SIGNATURES.map(([k, label]) => [label, sigs[k] || '']) },
      ].filter(Boolean),
    });
  };

  // ─── LIST VIEW ────────────────────────────────────────────────────────────
  if (!open) {
    const withPpm = new Set(list.map((r) => r.gpo_no));
    const shown = list.filter((r) => matchesGpo(gpoQ, r.gpo_no, r.ppm_no, r.estimate_no, r.style_no));
    return (
      <div>
        <div style={{ ...cardStyle, background: VIOLET, border: 'none', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>📋 Open PPM:</div>
          <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 260, background: '#fff' }}>
            <option value="">Select a GPO…</option>
            {gpos.map((g) => <option key={g.gpo_no} value={g.gpo_no}>GPO {g.gpo_no} — {g.style_no}{g.brand ? ' (' + g.brand + ')' : ''}{withPpm.has(g.gpo_no) ? ' ✓' : ''}</option>)}
          </select>
          <button type="button" disabled={!pick || saving} onClick={() => { openFor(pick); setPick(''); }}
            style={{ padding: '8px 18px', background: pick ? INK : '#bbb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: pick ? 'pointer' : 'default' }}>
            {saving ? 'Opening…' : 'Open →'}
          </button>
          <span style={{ fontSize: 12, color: '#888' }}>PPM unlocks once Full Kitting is Complete.</span>
        </div>
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px 0' }}>
            <GpoFilter value={gpoQ} onChange={setGpoQ} />
            <FilterCount shown={shown.length} total={list.length} noun="PPMs" q={gpoQ} />
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
              {['PPM No', 'Order', 'Style / Buyer', 'Status', 'Updated', ''].map((h) => <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {!shown.length && <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>{list.length ? 'No PPM matches that GPO.' : 'No PPMs yet — open one above (requires Full Kitting complete).'}</td></tr>}
              {shown.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: NAVY }}>{r.ppm_number || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>{r.estimate_no || '—'}{r.gpo_no ? <span style={{ color: '#999' }}> · GPO {r.gpo_no}</span> : ''}</td>
                  <td style={{ padding: '10px 14px' }}>{r.style_no || '—'}{r.brand ? <span style={{ color: '#999' }}> · {r.brand}</span> : ''}</td>
                  <td style={{ padding: '10px 14px' }}>{r.status === 'completed' ? <span style={{ color: '#0F7B3F', fontWeight: 700 }}>✓ Completed</span> : <span style={{ color: '#854F0B', fontWeight: 700 }}>● Draft</span>}</td>
                  <td style={{ padding: '10px 14px', color: '#888', fontSize: 12 }}>{fmtDate(r.updated_at)}</td>
                  <td style={{ padding: '10px 14px' }}><button type="button" onClick={() => openFor(r.gpo_no)} style={{ padding: '5px 14px', background: VIOLET, color: INK, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Open →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListFooter shown={list.length} total={list.length} noun="PPMs" />
        </div>
      </div>
    );
  }

  // ─── DETAIL VIEW ──────────────────────────────────────────────────────────
  const p = open.ppm, gpoNo = header.gpo_no || p.gpo_no, estimateId = p.estimate_id;
  const gateOk = !open.gate || open.gate.allowed;
  return (
    <div>
      {/* Header bar + gate indicator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <button type="button" onClick={() => { setOpen(null); fetchList(); }} style={{ padding: '6px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, color: NAVY, fontSize: 15 }}>{p.ppm_number}</span>
          <button type="button" disabled={saving} onClick={save} style={{ padding: '7px 16px', background: INK, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : '💾 Save'}</button>
          <button type="button" onClick={printPack} style={printBtnStyle}>🖨️ Print Combined Pack (PDF)</button>
        </div>
      </div>
      <div style={{ background: gateOk ? '#F2FBF5' : '#FBE9E9', border: `1px solid ${gateOk ? '#B8E6C8' : '#F5C2C2'}`, borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 13, color: gateOk ? '#0F7B3F' : RED, fontWeight: 600 }}>
        {gateOk ? '✓ Full Kitting Complete — PPM gate is open.' : '🔒 Full Kitting incomplete — PPM is blocked.'}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '2px solid #eee' }}>
        {[['ppm', '📋 PPM Form'], ['checklist', '✅ Production File Checklist']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 16px', border: 'none', background: 'none', borderBottom: tab === k ? `3px solid ${INK}` : '3px solid transparent', color: tab === k ? INK : '#888', fontWeight: tab === k ? 700 : 500, fontSize: 14, cursor: 'pointer', marginBottom: -2 }}>{label}</button>
        ))}
      </div>

      {/* ── PPM FORM TAB ── */}
      {tab === 'ppm' && (
        <div>
          <RiskAlertBanner doc="PPM" gpoNo={gpoNo} estimateId={estimateId} />

          {/* Header (auto-filled, editable) */}
          <div style={cardStyle}>
            <h3 style={titleStyle}>Order Header <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#aaa', fontSize: 11 }}>— auto-filled from order, editable</span></h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
              {[['buyer', 'Buyer'], ['brand', 'Brand'], ['style_no', 'Style No'], ['style_ref', 'Style Ref'], ['item_desc', 'Item Description'], ['total_qty', 'Total Qty']].map(([k, label]) => (
                <div key={k}><label style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{label}</label>
                  <input style={inputStyle} value={header[k] != null ? header[k] : ''} onChange={(e) => setHeader((h) => ({ ...h, [k]: e.target.value }))} /></div>
              ))}
            </div>
            {/* PO breakdown */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: INK, textTransform: 'uppercase', marginBottom: 6 }}>PO Breakdown (1st–5th)</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: WARM, color: '#666' }}>{['PO', 'Buyer PO No', 'Qty', 'Combo', 'Delivery'].map((h) => <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {(header.po_breakdown || []).map((po, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}>{po.label}</td>
                      {['buyer_po_no', 'qty', 'combo'].map((f) => <td key={f} style={{ padding: '4px 8px' }}><input style={inputStyle} value={po[f] != null ? po[f] : ''} onChange={(e) => setHeader((h) => ({ ...h, po_breakdown: h.po_breakdown.map((x, xi) => xi === i ? { ...x, [f]: e.target.value } : x) }))} /></td>)}
                      <td style={{ padding: '4px 8px' }}><input type="date" style={inputStyle} value={po.delivery_date ? String(po.delivery_date).slice(0, 10) : ''} onChange={(e) => setHeader((h) => ({ ...h, po_breakdown: h.po_breakdown.map((x, xi) => xi === i ? { ...x, delivery_date: e.target.value } : x) }))} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Fabric details */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: INK, textTransform: 'uppercase', marginBottom: 6 }}>Fabric Details (up to 5)</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: WARM, color: '#666' }}>{['Name', 'Blend', 'GSM', 'Width', 'Finish', 'Wash', 'Target CPP'].map((h) => <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {(header.fabrics && header.fabrics.length ? header.fabrics : [{}]).map((f, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                      {['name', 'blend', 'gsm', 'width', 'finish', 'wash', 'target_cpp'].map((k) => <td key={k} style={{ padding: '4px 8px' }}><input style={inputStyle} value={f[k] != null ? f[k] : ''} onChange={(e) => setHeader((h) => { const fab = (h.fabrics && h.fabrics.length ? [...h.fabrics] : [{}]); fab[i] = { ...fab[i], [k]: e.target.value }; return { ...h, fabrics: fab }; })} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" onClick={() => setHeader((h) => ({ ...h, fabrics: [...(h.fabrics || []), {}] }))} disabled={(header.fabrics || []).length >= 5}
                style={{ marginTop: 6, padding: '4px 12px', background: '#fff', color: INK, border: `1px solid ${INK}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>+ Add fabric</button>
            </div>
          </div>

          {/* Sections A–F */}
          {SECTIONS.map((S) => (
            <div key={S.key} style={cardStyle}>
              <h3 style={titleStyle}>{S.title}</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>{['Particulars', 'Parameters', 'Comments'].map((h) => <th key={h} style={{ padding: '7px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {S.items.map(([ik, label]) => (
                    <tr key={ik} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '5px 10px', fontWeight: 600, color: '#444', width: '32%' }}>{label}</td>
                      <td style={{ padding: '5px 10px' }}><input style={inputStyle} value={(sec(S.key).params || {})[ik] || ''} onChange={(e) => setSecVal(S.key, 'params', ik, e.target.value)} /></td>
                      <td style={{ padding: '5px 10px' }}><input style={inputStyle} value={(sec(S.key).comments || {})[ik] || ''} onChange={(e) => setSecVal(S.key, 'comments', ik, e.target.value)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                {S.machine && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#854F0B' }}>Machine servicing required?</span>
                    {['Yes', 'No', 'NA'].map((v) => (
                      <button key={v} type="button" onClick={() => setSecField(S.key, 'machine_servicing', v)}
                        style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: (sec(S.key).machine_servicing || 'NA') === v ? `1px solid ${INK}` : '1px solid #ddd', background: (sec(S.key).machine_servicing || 'NA') === v ? VIOLET : '#fff', color: (sec(S.key).machine_servicing || 'NA') === v ? INK : '#888' }}>{v}</button>
                    ))}
                    <span style={{ fontSize: 10, color: '#bbb' }}>(links to Maintenance later)</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Signature:</span>
                  <input style={{ ...inputStyle, maxWidth: 260 }} value={sec(S.key).signature || ''} onChange={(e) => setSecField(S.key, 'signature', e.target.value)} placeholder="Name / sign" />
                </div>
              </div>
            </div>
          ))}

          {/* Attendance */}
          <div style={cardStyle}>
            <h3 style={titleStyle}>Attendance Sheet</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>{['Name', 'Designation', 'Department', ''].map((h) => <th key={h} style={{ padding: '7px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
              <tbody>
                {attendance.map((a, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                    {['name', 'designation', 'department'].map((k) => <td key={k} style={{ padding: '4px 10px' }}><input style={inputStyle} value={a[k] || ''} onChange={(e) => setAttendance((arr) => arr.map((x, xi) => xi === i ? { ...x, [k]: e.target.value } : x))} /></td>)}
                    <td style={{ padding: '4px 10px' }}><button type="button" onClick={() => setAttendance((arr) => arr.filter((_, xi) => xi !== i))} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: 16 }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" onClick={() => setAttendance((arr) => [...arr, { name: '', designation: '', department: '' }])} style={{ marginTop: 8, padding: '5px 14px', background: '#fff', color: INK, border: `1px solid ${INK}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>+ Add attendee</button>
          </div>
        </div>
      )}

      {/* ── CHECKLIST TAB ── */}
      {tab === 'checklist' && (
        <div>
          <RiskAlertBanner doc="ProductionFileChecklist" gpoNo={gpoNo} estimateId={estimateId} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button type="button" onClick={refreshAuto} style={printBtnStyle}>🔄 Refresh auto-ticks</button>
          </div>
          {STAGES.map((st) => {
            const rows = (open.items || []).filter((i) => i.stage === st.n);
            if (!rows.length) return null;
            return (
              <div key={st.n} style={cardStyle}>
                <h3 style={titleStyle}>{st.label}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>{['Item', 'Resp', 'Done', 'Comments'].map((h) => <th key={h} style={{ padding: '7px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {rows.map((it) => (
                      <tr key={it.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: '#222' }}>{it.item_name}</td>
                        <td style={{ padding: '6px 10px' }}><span title={RESP_LABEL[it.responsibility] || ''} style={{ background: VIOLET, color: INK, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{it.responsibility || '—'}</span></td>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={!!(it.checked || it.auto_checked)} disabled={it.auto_checked}
                            onChange={(e) => toggleItem(it, e.target.checked)} style={{ width: 17, height: 17, verticalAlign: 'middle', accentColor: INK, cursor: it.auto_checked ? 'not-allowed' : 'pointer' }} />
                          {it.auto_checked && <span style={{ marginLeft: 6, background: '#DDF3E4', color: '#0F7B3F', padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>auto</span>}
                        </td>
                        <td style={{ padding: '6px 10px' }}><input style={inputStyle} value={it.comments || ''} onChange={(e) => setItemComment(it, e.target.value)} onBlur={() => saveItemComment(it)} placeholder="—" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Six signatures */}
          <div style={cardStyle}>
            <h3 style={titleStyle}>File Checklist Signatures</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
              {SIGNATURES.map(([k, label]) => (
                <div key={k}><label style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{label}</label>
                  <input style={inputStyle} value={sigs[k] || ''} onChange={(e) => setSigs((s) => ({ ...s, [k]: e.target.value }))} placeholder="Name / sign" /></div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}><button type="button" disabled={saving} onClick={save} style={{ padding: '8px 18px', background: INK, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>💾 Save signatures</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
